import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { get } from '@/api/client';
import { Avatar } from '@/components/atoms/Avatar';
import { LevelBadge } from '@/components/atoms/LevelBadge';
import { Skeleton } from '@/components/atoms/Skeleton';
import { RateText } from '@/components/atoms/RateText';
import { AdBanner } from '@/components/ads/AdBanner';
import { comma } from '@/lib/format';
import { useAuthStore } from '@/store/authStore';
import { Trophy, Timer } from 'lucide-react';
import { ServiceNav } from '@/components/organisms/ServiceNav';
import { SEOHead } from '@/components/atoms/SEOHead';

/* ── 타입 ─────────────────────────────── */

interface ScoreRanker {
  user_id: string;
  nickname: string;
  profile_image_url: string | null;
  level: number;
  score: number;
}

interface VirtualRanker {
  rank: number;
  user_id: string;
  nickname: string;
  profile_image_url: string | null;
  level: number;
  total_return_rate: number;
  total_profit: number;
  portfolio_value: number;
}

interface Season {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
}

interface MyRanking {
  season: { rank: number; totalReturnRate: number; totalProfit: number } | null;
  alltime: { rank: number; totalReturnRate: number; totalProfit: number } | null;
}

/* ── 컴포넌트 ─────────────────────────── */

type Tab = 'score' | 'season' | 'alltime';

const MEDAL = ['🥇', '🥈', '🥉'];

export function RankingPage() {
  const [tab, setTab] = useState<Tab>('season');
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);

  const scoreQuery = useQuery({
    queryKey: ['ranking', 'score'],
    queryFn: () => get<{ list: ScoreRanker[] }>('/users?sort=score&limit=100'),
    enabled: tab === 'score',
  });

  const seasonQuery = useQuery({
    queryKey: ['ranking', 'virtual', 'season'],
    queryFn: () =>
      get<{ season: Season | null; list: VirtualRanker[] }>('/virtual/ranking/season'),
    enabled: tab === 'season',
  });

  const alltimeQuery = useQuery({
    queryKey: ['ranking', 'virtual', 'alltime'],
    queryFn: () => get<{ list: VirtualRanker[] }>('/virtual/ranking/alltime'),
    enabled: tab === 'alltime',
  });

  const myQuery = useQuery({
    queryKey: ['ranking', 'virtual', 'me'],
    queryFn: () => get<MyRanking>('/virtual/ranking/me'),
    enabled: isLoggedIn && (tab === 'season' || tab === 'alltime'),
  });

  const season = seasonQuery.data?.season;
  const isLoading =
    (tab === 'score' && scoreQuery.isLoading) ||
    (tab === 'season' && seasonQuery.isLoading) ||
    (tab === 'alltime' && alltimeQuery.isLoading);

  return (
    <div className="container-base pt-4 pb-6">
      <SEOHead
        title="랭킹"
        description="시세공간 모의투자 랭킹 — 시즌별/누적 수익률 상위 트레이더 확인"
        canonical="/ranking"
      />
      <ServiceNav />
      <h1 className="mb-4 text-2xl font-extrabold">랭킹</h1>

      {/* 탭 */}
      <div className="mb-5 flex gap-2">
        {([
          { key: 'season' as const, label: '이번 달 시즌' },
          { key: 'alltime' as const, label: '누적 전체' },
          { key: 'score' as const, label: '활동 점수' },
        ]).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${
              tab === key
                ? 'bg-[var(--color-primary)] text-white'
                : 'bg-[var(--color-bg)] text-[var(--color-text-sub)] hover:text-[var(--color-text)]'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* 시즌 정보 + 내 순위 */}
      {tab === 'season' && season && (
        <SeasonBanner season={season} myRank={myQuery.data?.season ?? null} />
      )}
      {tab === 'alltime' && myQuery.data?.alltime && (
        <MyRankBanner label="누적" data={myQuery.data.alltime} />
      )}

      {/* 로딩 */}
      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      )}

      {/* 활동 점수 랭킹 */}
      {tab === 'score' && scoreQuery.data && (
        <ol className="divide-y divide-[var(--color-border)] rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
          {scoreQuery.data.list.map((r, i) => (
            <li key={r.user_id}>
              <Link
                to={`/users/${r.user_id}`}
                className="flex items-center gap-3 p-3 hover:bg-[var(--color-bg)]"
              >
                <RankBadge rank={i + 1} />
                <Avatar src={r.profile_image_url ?? undefined} alt={r.nickname} size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold truncate">{r.nickname}</span>
                    <LevelBadge level={r.level} size="sm" />
                  </div>
                </div>
                <div className="text-sm font-bold text-[var(--color-primary)]">
                  {comma(r.score)}점
                </div>
              </Link>
            </li>
          ))}
          {scoreQuery.data.list.length === 0 && <EmptyRow />}
        </ol>
      )}

      {/* 시즌 모의투자 랭킹 */}
      {tab === 'season' && seasonQuery.data && (
        <VirtualRankList list={seasonQuery.data.list} />
      )}

      {/* 누적 모의투자 랭킹 */}
      {tab === 'alltime' && alltimeQuery.data && (
        <VirtualRankList list={alltimeQuery.data.list} />
      )}

      {/* 페이지 하단 광고 — multiplex-h 전역 비활성화로 display-rect 로 교체 (2026-05-29) */}
      <AdBanner slot="display-rect" className="mt-4 sm:mt-8" />
    </div>
  );
}

/* ── 하위 컴포넌트 ───────────────────── */

function RankBadge({ rank }: { rank: number }) {
  return (
    <div className="w-8 text-center text-lg font-bold">
      {rank <= 3 ? (
        <span>{MEDAL[rank - 1]}</span>
      ) : (
        <span className="text-sm text-[var(--color-text-sub)]">{rank}</span>
      )}
    </div>
  );
}

function SeasonBanner({
  season,
  myRank,
}: {
  season: Season;
  myRank: { rank: number; totalReturnRate: number; totalProfit: number } | null;
}) {
  const endDate = new Date(season.end_date + 'T23:59:59+09:00');
  const now = new Date();
  const daysLeft = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / 86400000));

  return (
    <div className="mb-4 rounded-xl border border-[var(--color-border)] bg-gradient-to-r from-[var(--color-primary)]/5 to-transparent p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-[var(--color-primary)]" />
          <span className="font-bold">{season.name}</span>
        </div>
        <div className="flex items-center gap-1 text-sm text-[var(--color-text-sub)]">
          <Timer className="h-3.5 w-3.5" />
          {daysLeft > 0 ? `${daysLeft}일 남음` : '종료'}
        </div>
      </div>
      <div className="mt-1 text-xs text-[var(--color-text-sub)]">
        {season.start_date} ~ {season.end_date} · 1위 500P / 2위 300P / 3위 200P / 10위 이내 100P
      </div>
      {myRank && (
        <div className="mt-3 flex items-center gap-4 rounded-lg bg-[var(--color-surface)] px-3 py-2 text-sm">
          <span className="font-semibold text-[var(--color-primary)]">내 순위 {myRank.rank}위</span>
          <RateText value={myRank.totalReturnRate} size="sm" />
          <span className="text-[var(--color-text-sub)]">
            {myRank.totalProfit >= 0 ? '+' : ''}
            {comma(Math.round(myRank.totalProfit))}원
          </span>
        </div>
      )}
    </div>
  );
}

function MyRankBanner({
  label,
  data,
}: {
  label: string;
  data: { rank: number; totalReturnRate: number; totalProfit: number };
}) {
  return (
    <div className="mb-4 flex items-center gap-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-sm">
      <span className="font-semibold text-[var(--color-primary)]">
        {label} 내 순위 {data.rank}위
      </span>
      <RateText value={data.totalReturnRate} size="sm" />
      <span className="text-[var(--color-text-sub)]">
        {data.totalProfit >= 0 ? '+' : ''}
        {comma(Math.round(data.totalProfit))}원
      </span>
    </div>
  );
}

function VirtualRankList({ list }: { list: VirtualRanker[] }) {
  return (
    <ol className="divide-y divide-[var(--color-border)] rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
      {list.map((r) => (
        <li key={r.user_id}>
          <Link
            to={`/users/${r.user_id}`}
            className="flex items-center gap-3 p-3 hover:bg-[var(--color-bg)]"
          >
            <RankBadge rank={r.rank} />
            <Avatar
              src={r.profile_image_url ?? undefined}
              alt={r.nickname}
              size="sm"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold truncate">{r.nickname}</span>
                <LevelBadge level={r.level} size="sm" />
              </div>
              <div className="text-xs text-[var(--color-text-sub)]">
                평가 {comma(Math.round(r.portfolio_value))}원
              </div>
            </div>
            <div className="shrink-0 text-right">
              <RateText value={r.total_return_rate} size="md" />
              <div
                className={`text-xs font-semibold tabular-nums ${
                  r.total_profit > 0
                    ? 'text-[var(--color-rise)]'
                    : r.total_profit < 0
                    ? 'text-[var(--color-fall)]'
                    : 'text-[var(--color-text-sub)]'
                }`}
              >
                {r.total_profit >= 0 ? '+' : ''}
                {comma(Math.round(r.total_profit))}원
              </div>
            </div>
          </Link>
        </li>
      ))}
      {list.length === 0 && <EmptyRow />}
    </ol>
  );
}

function EmptyRow() {
  return (
    <li className="py-12 text-center text-sm text-[var(--color-text-sub)]">
      아직 랭킹 데이터가 없어요.
    </li>
  );
}
